import {
  Alert,
  Box,
  Chip,
  FormControlLabel,
  FormHelperText,
  Link,
  Switch,
  Typography,
} from "@mui/material";
import { grey } from "@mui/material/colors";
import { VaSettingsContext } from "contexts/VaSettings";
import { Dispatch, SetStateAction, useContext } from "react";
import {
  BulkSendSettingsStatus,
  WarmupConfig,
  WarmupConfigReason,
} from "schemas/dashboard";

import { SelectedSetting } from "../schema";

function EmailWarmupSection({
  enabled,
  onToggle,
  warmupConfig,
  setSelectedSetting,
}: {
  enabled: boolean;
  onToggle: (checked: boolean) => void;
  warmupConfig?: WarmupConfig;
  setSelectedSetting: Dispatch<SetStateAction<SelectedSetting>>;
}) {
  const { bulkSendSetting } = useContext(VaSettingsContext);
  const cycle = warmupConfig?.cycle ?? [];
  const currentStepIndex = warmupConfig?.currentStepIndex ?? 0;
  const isCompleted = !!warmupConfig?.lastCompletedAt;
  const startedAt = warmupConfig?.startedAt ?? null;

  // Calculate the scheduled start date for each step by accumulating days.
  // Step 0 starts at startedAt; each subsequent step starts after the previous
  // step's `days` have elapsed.
  const stepDates: (Date | null)[] = [];
  let cumulativeDays = 0;
  for (const step of cycle) {
    stepDates.push(
      startedAt ? new Date((startedAt + cumulativeDays * 86400) * 1000) : null,
    );
    cumulativeDays += step.days;
  }

  const formatDate = (date: Date | null) => {
    if (!date) return "TBD";
    return date.toLocaleDateString(undefined, {
      month: "short",
      day: "numeric",
    });
  };

  return (
    <>
      <Typography
        variant="h6"
        sx={{ fontSize: "15px", mt: 1, fontWeight: 600 }}
        gutterBottom
      >
        Email Warmup
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Email warmup gradually increases your sending volume over time to build
        a strong sender reputation and improve email deliverability.
      </Typography>

      <FormControlLabel
        control={
          <Switch
            checked={enabled}
            onChange={(e) => onToggle(e.target.checked)}
          />
        }
        label="Opt in to Email Warmup"
      />
      <FormHelperText sx={{ color: grey[700], mb: 3 }}>
        When enabled, your email sending volume will gradually increase each
        week to warm up your email address.
      </FormHelperText>

      {enabled && cycle.length > 0 && (
        <Box
          sx={{
            p: 2,
            border: "1px solid",
            borderColor: "divider",
            borderRadius: 2,
          }}
        >
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              mb: 1.5,
            }}
          >
            <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
              Warmup Schedule
            </Typography>
            {isCompleted && (
              <Chip label="Complete" color="success" size="small" />
            )}
          </Box>

          <Box sx={{ display: "flex", flexDirection: "column", gap: 0 }}>
            {cycle.map((step, i) => {
              const isPast = isCompleted || i < currentStepIndex;
              const isCurrent = !isCompleted && i === currentStepIndex;

              return (
                <Box
                  key={i}
                  sx={{ display: "flex", alignItems: "stretch", gap: 1.5 }}
                >
                  {/* Timeline track */}
                  <Box
                    sx={{
                      display: "flex",
                      flexDirection: "column",
                      alignItems: "center",
                      flexShrink: 0,
                    }}
                  >
                    {/* Dot */}
                    <Box
                      sx={{
                        width: 10,
                        height: 10,
                        borderRadius: "50%",
                        mt: "5px",
                        flexShrink: 0,
                        bgcolor: isPast
                          ? "success.main"
                          : isCurrent
                            ? "primary.main"
                            : "grey.300",
                      }}
                    />
                    {/* Connector line — hide after last item */}
                    {i < cycle.length - 1 && (
                      <Box
                        sx={{
                          width: 2,
                          flex: 1,
                          minHeight: 18,
                          bgcolor: isPast ? "success.light" : "grey.200",
                          my: 0.25,
                        }}
                      />
                    )}
                  </Box>

                  {/* Step content */}
                  <Box sx={{ pb: i < cycle.length - 1 ? 1 : 0, flex: 1 }}>
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        gap: 1,
                        flexWrap: "wrap",
                      }}
                    >
                      <Typography
                        variant="body2"
                        sx={{
                          fontWeight: isCurrent ? 600 : 400,
                          color: isPast
                            ? "text.secondary"
                            : isCurrent
                              ? "text.primary"
                              : "text.secondary",
                          minWidth: 52,
                        }}
                      >
                        {formatDate(stepDates[i])}
                      </Typography>
                      <Typography
                        variant="body2"
                        sx={{
                          color: isPast
                            ? "text.disabled"
                            : isCurrent
                              ? "primary.main"
                              : "text.secondary",
                          fontWeight: isCurrent ? 600 : 400,
                        }}
                      >
                        {step.emailVolume} emails / week
                        {isCurrent && bulkSendSetting?.addOnEmailVolume ? (
                          <>
                            {" "}
                            + {bulkSendSetting.addOnEmailVolume} purchased
                            add-on emails
                          </>
                        ) : null}
                      </Typography>
                      {isCurrent && (
                        <Chip
                          label="Current"
                          size="small"
                          color="primary"
                          sx={{ height: 18, fontSize: "0.65rem" }}
                        />
                      )}
                    </Box>
                  </Box>
                </Box>
              );
            })}
          </Box>
        </Box>
      )}

      {enabled &&
        cycle.length === 0 &&
        bulkSendSetting?.status === BulkSendSettingsStatus.inactive && (
          <Alert severity="info" sx={{ mt: 2 }}>
            We will start your email warmup schedule once you{" "}
            <strong>resume brand outreach</strong>. To resume, click{" "}
            <Link
              sx={{ cursor: "pointer" }}
              onClick={() => setSelectedSetting(SelectedSetting.general)}
            >
              here
            </Link>
            .
          </Alert>
        )}

      {enabled && warmupConfig?.reason === WarmupConfigReason.email_blocked && (
        <Alert severity="error" sx={{ mt: 2 }}>
          We detected that your email has hit rate sending limit / or
          consecutive blocks. To protect your sender reputation, we have put you
          on an email warmup cycle and decrease your sending rate. If you have
          any questions, please do not hesitate and reach out to us at{" "}
          <a href="mailto:va@onbento.com">va@onbento.com</a>
        </Alert>
      )}
    </>
  );
}

export default EmailWarmupSection;
